import type { ChangeEvent, DragEvent, MouseEventHandler } from "react";
import { Upload as AntUpload, type UploadProps } from "antd";

import type { AvE2EProps } from "@av/components/types";

type UploadDraggerAccessibility = {
  label?: string;
  invalid?: boolean;
  describedBy?: string;
};

type UploadDraggerProps = AvE2EProps &
  UploadProps & {
    accessibility?: UploadDraggerAccessibility;
    fileInputTestId?: string;
    onChangeCapture?: (event: ChangeEvent<HTMLDivElement>) => void;
    onClick?: MouseEventHandler<HTMLDivElement>;
    onDropCapture?: (event: DragEvent<HTMLDivElement>) => void;
  };

export function UploadDragger({
  testIdPrefix,
  children,
  accessibility,
  disabled,
  fileInputTestId,
  onChangeCapture,
  onClick,
  onDropCapture,
  ...uploadProps
}: UploadDraggerProps) {
  return (
    <div
      data-testid={testIdPrefix}
      role="group"
      aria-label={accessibility?.label}
      aria-invalid={accessibility?.invalid || undefined}
      aria-describedby={accessibility?.describedBy}
      onChangeCapture={onChangeCapture}
      onClick={onClick}
      onDragEnterCapture={(event) => {
        if (!disabled) {
          return;
        }

        event.dataTransfer.dropEffect = "none";
        event.preventDefault();
        event.stopPropagation();
      }}
      onDragOverCapture={(event) => {
        if (!disabled) {
          return;
        }

        event.dataTransfer.dropEffect = "none";
        event.preventDefault();
        event.stopPropagation();
      }}
      onDragLeaveCapture={(event) => {
        if (!disabled) {
          return;
        }

        event.dataTransfer.dropEffect = "none";
        event.preventDefault();
        event.stopPropagation();
      }}
      onDropCapture={(event) => {
        if (disabled) {
          event.dataTransfer.dropEffect = "none";
          event.preventDefault();
          event.stopPropagation();

          return;
        }

        onDropCapture?.(event);
      }}
    >
      <AntUpload.Dragger
        aria-label={accessibility?.label}
        aria-invalid={accessibility?.invalid || undefined}
        aria-describedby={accessibility?.describedBy}
        data-testid={fileInputTestId}
        disabled={disabled}
        {...uploadProps}
      >
        {children}
      </AntUpload.Dragger>
    </div>
  );
}
